/**
 * BulkUploadStackProvider — provider React de la pile globale d'uploads.
 *
 * À placer haut dans l'arbre — branché au niveau `AppShell` pour qu'il couvre
 * toutes les routes authentifiées et survive aux changements de route. Une
 * seule pile suffit (un upload œuvre + un upload mockup en parallèle = deux
 * batches dans la même pile, l'overlay les empile naturellement).
 *
 * Le provider rend lui-même `<BulkUploadStackOverlay>` en interne — pas
 * besoin d'inclure l'overlay dans le JSX d'AppShell.
 *
 * **Règles non négociables** (cf incident 2026-05-09) :
 *
 * 1. Le flag d'annulation vit dans un `useRef<Map<id, boolean>>`, pas dans
 *    le state. Le predicate passé à `_runWithConcurrency` est une closure
 *    capturée à l'enqueue ; lire un state via closure ne verrait jamais
 *    l'update — c'est exactement le bug d'origine.
 *
 * 2. Tout update du progress passe par `setBatches(prev => …)` en lisant
 *    `prev.progress` du callback, jamais une closure. Sans ça, deux workers
 *    qui terminent simultanément se piétinent (StrictMode + concurrent React).
 *
 * 3. `onComplete` est appelé même en cas de cancel, avec summary partiel.
 *    Sans ça, les fichiers déjà uploadés n'apparaîtraient pas (queries non
 *    invalidées).
 *
 * 4. Le listener `beforeunload` est attaché conditionnellement à
 *    `hasInflight` (booléen dérivé via useMemo) — pas en permanence, sinon
 *    on bloquerait la navigation pendant toute la vie de l'onglet après
 *    le 1er upload.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import {
  BulkUploadStackContext,
  EMPTY_COUNTS,
  countByStatus,
  type BulkFileProgress,
  type BulkUploadBatch,
  type BulkUploadStackContextValue,
  type BulkUploadSummary,
  type EnqueueInput,
  type MappedError,
} from './bulk-upload-stack-context'
import { _runWithConcurrency } from '@/components/upload/_runWithConcurrency'
import BulkUploadStackOverlay from '@/components/upload/BulkUploadStackOverlay'

const DEFAULT_CONCURRENCY = 2
const AUTO_DISMISS_MS = 5_000

const DEFAULT_MAP_ERROR = (error: unknown): MappedError => {
  if (!error || typeof error !== 'object') {
    return { status: 'error', message: String(error) }
  }
  const apiErr = error as { detail?: unknown }
  const detail = typeof apiErr.detail === 'string' ? apiErr.detail : 'Erreur inconnue'
  return { status: 'error', message: detail }
}

interface BulkUploadStackProviderProps {
  children: ReactNode
}

export function BulkUploadStackProvider({ children }: BulkUploadStackProviderProps) {
  const [batches, setBatches] = useState<BulkUploadBatch[]>([])
  // `expanded` est local au provider — pas exposé dans le context. Sert à
  // mettre en pause l'auto-dismiss quand l'utilisatrice consulte la liste
  // détaillée (sinon le batch disparaît sous le doigt pendant qu'elle lit).
  const [expanded, setExpanded] = useState(false)

  // Flag d'annulation — Map plutôt qu'objet pour mutations ergonomiques.
  // Lu par le predicate de `_runWithConcurrency` capturé à l'enqueue.
  const cancelledRef = useRef<Map<string, boolean>>(new Map())

  // Source originale de chaque batch — sert à `retry(id)` pour ré-enqueue
  // les fichiers en échec avec mêmes uploader/mapError/onComplete capturés.
  const sourceInputsRef = useRef<Map<string, EnqueueInput<unknown>>>(new Map())

  // Timer ID du `setTimeout(AUTO_DISMISS_MS)` par batch — pour pouvoir
  // annuler quand `expanded` passe à true.
  const dismissTimersRef = useRef<Map<string, number>>(new Map())

  // ──────────────────────────────────────────────────────────────────────
  // Auto-dismiss timer plumbing — pause quand expanded
  // ──────────────────────────────────────────────────────────────────────

  const clearDismissTimer = useCallback((id: string) => {
    const timer = dismissTimersRef.current.get(id)
    if (timer !== undefined) {
      window.clearTimeout(timer)
      dismissTimersRef.current.delete(id)
    }
  }, [])

  const dismissInternal = useCallback(
    (id: string) => {
      clearDismissTimer(id)
      cancelledRef.current.delete(id)
      sourceInputsRef.current.delete(id)
      setBatches((prev) => prev.filter((b) => b.id !== id))
    },
    [clearDismissTimer],
  )

  const scheduleAutoDismiss = useCallback(
    (id: string) => {
      // Si déjà programmé, ne pas re-programmer.
      if (dismissTimersRef.current.has(id)) return
      const timer = window.setTimeout(() => {
        dismissTimersRef.current.delete(id)
        dismissInternal(id)
      }, AUTO_DISMISS_MS)
      dismissTimersRef.current.set(id, timer)
    },
    [dismissInternal],
  )

  const isAutoDismissEligible = useCallback((batch: BulkUploadBatch): boolean => {
    if (batch.phase !== 'done') return false
    if (batch.cancelled) return false
    return batch.counts.error + batch.counts.duplicate_slug === 0
  }, [])

  // Pause/resume auto-dismiss timers selon `expanded` :
  // - Quand expanded passe à true → clear tous les timers actifs.
  // - Quand expanded repasse à false → relance pour les batches éligibles.
  useEffect(() => {
    if (expanded) {
      for (const id of Array.from(dismissTimersRef.current.keys())) {
        clearDismissTimer(id)
      }
      return
    }
    // Collapsed — relance les timers pour les batches done éligibles.
    for (const batch of batches) {
      if (isAutoDismissEligible(batch)) {
        scheduleAutoDismiss(batch.id)
      }
    }
  }, [expanded, batches, clearDismissTimer, scheduleAutoDismiss, isAutoDismissEligible])

  // ──────────────────────────────────────────────────────────────────────
  // beforeunload — bloque la fermeture d'onglet pendant un upload in-flight
  // ──────────────────────────────────────────────────────────────────────

  const hasInflight = useMemo(() => batches.some((b) => b.phase === 'uploading'), [batches])

  useEffect(() => {
    if (!hasInflight) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Requis pour Firefox legacy ; les browsers modernes ignorent toute
      // chaîne custom et affichent leur prompt générique (Chrome 51+).
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasInflight])

  // ──────────────────────────────────────────────────────────────────────
  // enqueue — pousse un nouveau batch + lance le pool en fire-and-forget
  // ──────────────────────────────────────────────────────────────────────

  // Pool d'un batch : crée le worker, lance _runWithConcurrency, finalise.
  // Défini AVANT enqueue (qui le consomme) pour éviter le TDZ — useCallback
  // n'est pas hoisté.
  const runBatch = useCallback(
    async <T,>(
      id: string,
      files: File[],
      uploader: (file: File) => Promise<T>,
      concurrencyLimit: number,
      mapError: (error: unknown, file: File) => MappedError,
      onComplete: ((summary: BulkUploadSummary) => void) | undefined,
    ) => {
      const updateFile = (index: number, patch: Partial<BulkFileProgress>) => {
        setBatches((prev) =>
          prev.map((b) => {
            if (b.id !== id) return b
            const nextProgress = [...b.progress]
            nextProgress[index] = { ...nextProgress[index], ...patch }
            return {
              ...b,
              progress: nextProgress,
              counts: countByStatus(nextProgress),
            }
          }),
        )
      }

      await _runWithConcurrency(
        files,
        concurrencyLimit,
        async (file, index) => {
          if (cancelledRef.current.get(id)) return
          updateFile(index, { status: 'uploading' })
          try {
            await uploader(file)
            updateFile(index, { status: 'success' })
          } catch (err) {
            const mapped = mapError(err, file)
            updateFile(index, { status: mapped.status, message: mapped.message })
          }
        },
        () => !cancelledRef.current.get(id),
      )

      // Finalisation — flip phase à 'done', cancelled si applicable,
      // calcule summary, appelle onComplete (même en cas de cancel),
      // programme auto-dismiss si éligible.
      const wasCancelled = cancelledRef.current.get(id) === true

      let finalProgress: readonly BulkFileProgress[] = []
      let finalCounts = EMPTY_COUNTS
      setBatches((prev) =>
        prev.map((b) => {
          if (b.id !== id) return b
          finalProgress = b.progress
          finalCounts = b.counts
          return { ...b, phase: 'done', cancelled: wasCancelled }
        }),
      )

      // Summary : reconstruit retryFiles depuis indices error/duplicate_slug.
      const retryFiles = files.filter((_, idx) => {
        const row = finalProgress[idx]
        return row?.status === 'error' || row?.status === 'duplicate_slug'
      })
      const summary: BulkUploadSummary = { counts: finalCounts, retryFiles }

      try {
        onComplete?.(summary)
      } catch (err) {
        // Pas de propagation — un onComplete qui plante ne doit pas casser
        // le state du provider. Logué pour debug.
        console.error('[BulkUploadStack] onComplete threw:', err)
      }

      // Auto-dismiss si éligible (succès complet, pas cancel) ET non expanded.
      const eligible = !wasCancelled && finalCounts.error + finalCounts.duplicate_slug === 0
      if (eligible && !expanded) {
        scheduleAutoDismiss(id)
      }
    },
    // expanded change → on ne ré-instancie pas runBatch. Le provider
    // expanded effect (ci-dessus) re-programme les timers à la transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduleAutoDismiss],
  )

  const enqueue = useCallback(
    <T,>(input: EnqueueInput<T>): string => {
      const id = crypto.randomUUID()
      const concurrency = input.concurrencyLimit ?? DEFAULT_CONCURRENCY
      const mapError = input.mapError ?? DEFAULT_MAP_ERROR

      const initialProgress: BulkFileProgress[] = input.files.map((f) => ({
        filename: f.name,
        status: 'pending',
      }))

      const initialBatch: BulkUploadBatch = {
        id,
        label: input.label,
        total: input.files.length,
        phase: 'uploading',
        progress: initialProgress,
        counts: countByStatus(initialProgress),
        cancelled: false,
        startedAt: Date.now(),
      }

      setBatches((prev) => [...prev, initialBatch])
      cancelledRef.current.set(id, false)
      // Cast pour stocker un EnqueueInput<unknown> uniforme dans la Map —
      // l'API publique reste typée par appel via le générique.
      sourceInputsRef.current.set(id, input as EnqueueInput<unknown>)

      // Pool en fire-and-forget — pas d'await ici (l'enqueue retourne
      // immédiatement, c'est le but du Drive-style).
      void runBatch(id, input.files, input.uploader, concurrency, mapError, input.onComplete)

      return id
    },
    [runBatch],
  )

  // ──────────────────────────────────────────────────────────────────────
  // cancel / dismiss / retry
  // ──────────────────────────────────────────────────────────────────────

  const cancel = useCallback((id: string) => {
    cancelledRef.current.set(id, true)
    // Pas de setBatches ici — la boucle `_runWithConcurrency` retourne
    // au prochain pull, runBatch finalise (flip phase=done, cancelled=true).
  }, [])

  const dismiss = useCallback(
    (id: string) => {
      // No-op si batch absent ou si encore en cours (cancel d'abord).
      const batch = batches.find((b) => b.id === id)
      if (!batch) return
      if (batch.phase === 'uploading') return
      dismissInternal(id)
    },
    [batches, dismissInternal],
  )

  const retry = useCallback(
    (id: string): string | null => {
      const source = sourceInputsRef.current.get(id)
      if (!source) return null
      const batch = batches.find((b) => b.id === id)
      if (!batch || batch.phase !== 'done') return null
      const failedIndices = batch.progress
        .map((p, i) => ({ p, i }))
        .filter(({ p }) => p.status === 'error' || p.status === 'duplicate_slug')
        .map(({ i }) => i)
      if (failedIndices.length === 0) return null
      const failedFiles = failedIndices.map((i) => source.files[i])
      // Strip un éventuel suffixe "— retry N" pour ne pas accumuler.
      const baseLabel = source.label.replace(/ — retry \d+$/, '')
      const newId = enqueue({
        ...source,
        files: failedFiles,
        label: `${baseLabel} — retry ${failedFiles.length}`,
      })
      dismissInternal(id)
      return newId
    },
    [batches, enqueue, dismissInternal],
  )

  // ──────────────────────────────────────────────────────────────────────
  // Cleanup global au démontage du provider — normalement jamais en prod
  // (le provider vit pour la durée de l'AppShell), mais nettoie les
  // timers en suspens pour les tests/StrictMode.
  // ──────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const timers = dismissTimersRef.current
    return () => {
      for (const timer of timers.values()) {
        window.clearTimeout(timer)
      }
      timers.clear()
    }
  }, [])

  const value: BulkUploadStackContextValue = {
    batches,
    enqueue,
    dismiss,
    cancel,
    retry,
  }

  return (
    <BulkUploadStackContext.Provider value={value}>
      {children}
      <BulkUploadStackOverlay
        batches={batches}
        expanded={expanded}
        onExpandedChange={setExpanded}
        onCancel={cancel}
        onDismiss={dismiss}
        onRetry={retry}
      />
    </BulkUploadStackContext.Provider>
  )
}
